<!-- Copyright (c) 2025 Digital Systems (M) Sdn. Bhd. -->

<template>
  <div class="main">
    <div class="left-menu-container">
      <Menus :menu="menu" icon="https://dsmlib.logistics.systems/Icons/Ready.png" @menuClicked="menuClicked"
    /></div>
    <div class="right-tablebox-container">
      <TableBox
        title="Purchase Orders"
        :tableData="purchaseOrderListFiltered"
        :selectedRow.sync="purchaseOrderTableSelectedRows"
        :loading="purchaseOrderTableLoading"
        :dataType="purchaseOrderListDataType"
        :columnOrderViews.sync="purchaseOrderTableColumnOrderViews"
        :columnOrderID="purchaseOrderTableColumnOrderID"
      />
    </div>
    <PopupEntry
      class="popup-entry"
      v-if="showPopup"
      :show.sync="showPopup"
      :title="popUpTitle"
      :viewMode="viewMode"
      :requiredError="popupEntryRequiredError"
      @requiredClicked="popupEntryRequiredClicked"
      :successMessages="popupEntrySuccessMessages"
      :errorMessages="popupEntryErrorMessages"
      @save="popupSave"
      :updatedBy="lastUpdatedInfo"
    >
      <div class="flex-container">
        <DateTimePicker
          placeholder="Date"
          :isRequired="true"
          :value.sync="poDateValue"
          :errorMessage.sync="poDateValueError"
          ref="PODate"
          :isDisabled="viewMode"
        />

        <Dropdown
          placeholder="Job Type"
          style="width: 20%"
          :items="jobTypeList"
          itemKey="Description"
          :selectedObject.sync="jobTypeValue"
          :isRequired="true"
          :errorMessage.sync="jobTypeValueError"
          ref="POType"
          :isDisabled="viewMode || (selectedPO ? Boolean(selectedPO.PONO) : false)"
        />
        <Input
          placeholder="Ref #"
          :inputValue.sync="refNoValue"
          style="width: 20%"
          validator="text, 25"
          :isDisabled="viewMode"
        />

        <div class="purchase-order-summary" style="margin-left: auto"
          >Total Amount: <b>{{ $currencyFormat(totalAmountValue) }}</b></div
        >
        <div class="purchase-order-summary">Status: <b>{{ statusValue }}</b></div>
        <div class="purchase-order-summary">Partial Delivery: <b>{{ partialDeliveryStatus }}</b></div>
      </div>
      <div class="flex-container">
        <InputQuery
          placeholder="Supplier"
          :selectedObject.sync="supplierValue"
          :isRequired="true"
          :items="supplierList"
          itemKey="label"
          :errorMessage.sync="supplierValueError"
          ref="supplier"
          :isDisabled="viewMode"
          :tableBoxHideColumns="['label', 'cocode']"
          :tableData="supplierDataType"
        />
        <Input
          ref="remarks"
          placeholder="Remarks"
          :inputValue.sync="remarksValue"
          :errorMessage.sync="remarksValueError"
          validator="text,200"
          :isDisabled="viewMode"
        />
      </div>
      <div class="tab-component-container" style="min-height: 0">
        <Tab
          :tabs="tabItems"
          :tabIndex.sync="selectedTabIndex"
          @tabClicked="tabClick"
          :preventTabSwitch="true"
          class="tab-component"
        >
          <div class="tab-container table-tab-container" :slot="productListTitle">
            <div class="table-container">
              <div class="cta-group" v-if="!viewMode"
                ><Button @click="openProductFields()" :disabled="selectedProduct">Add</Button>
                <Button :disabled="selectedProduct || productListTableSelectedRows.length < 1" @click="productDelete"
                  >Delete</Button
                >
              </div>
              <TableBox
                style="flex: 1; min-height: 0"
                title="Product List"
                :tableData="productListFiltered"
                :selectedRow.sync="productListTableSelectedRows"
                :additionalButtons="tableAdditionalButton"
                @buttonClicked="productListTableButtonClick"
                :hideColumns="['SeqNo']"
                :dataType="productListDataType"
                :columnOrderViews.sync="productListColumnOrderViews"
                :columnOrderID="productListColumnOrderID"
              />
            </div>
            <div class="PO-form" v-if="selectedProduct">
              <div class="cta-group"
                ><Button @click="productSave" v-if="!viewMode">Save</Button>
                <Button @click="selectedProduct = null">Cancel</Button>
              </div>
              <div class="flex-container" style="align-items: center">
                <InputQuery
                  placeholder="Product"
                  :items="whProductListFiltered"
                  itemKey="label"
                  :tableBoxDataType="whProductListDataType"
                  :selectedObject.sync="whProductValue"
                  :isRequired="true"
                  @selected="whProductValueChanged"
                  :tableBoxHideColumns="[
                    'Product ID',
                    'Sub',
                    'Metric',
                    'DefaultUnitDescription',
                    'subPackCode1',
                    'Sub1UnitDescription',
                    'subPackCode2',
                    'Sub2UnitDescription',
                    'subQtyUnit2',
                    'subQtyUnit3',
                    'QtyOnHand',
                    'label',
                    'ItemTypeKey',
                    'ARTaxCode',
                    'ARTaxDesc',
                    'ARTaxRate',
                    'ARTaxType',
                    'APTaxCode',
                    'APTaxDesc',
                    'APTaxRate',
                    'APTaxType',
                  ]"
                  :errorMessage.sync="whProductValueError"
                  :isDisabled="viewMode"
                />
                <CheckBox
                  style="flex: 1 0 auto; margin-right: 8px"
                  title="Loose Item"
                  :isChecked.sync="looseItemValue"
                  :isDisabled="viewMode"
                />
              </div>
              <BoxContainer title="Total Quantity">
                <div class="total-qty-row" v-if="hasCartonConversion(whProductValue)">
                  <div>
                    <Input
                      :placeholder="
                        whProductValue ?
                          `[${whProductValue.subPackCode2}] ${whProductValue.Sub2UnitDescription}`
                        : 'subPack2'
                      "
                      :inputValue.sync="cartonValue"
                      :isRequired="true"
                      validator="positiveDecimal,24,4"
                      :isDisabled="!whProductValue || viewMode"
                    />
                  </div>
                  <div> x </div>
                  <div style="flex: 1 0 auto">
                    <div>
                      Qty Per
                      {{ whProductValue ? `${whProductValue.subPackCode2}` : 'subPack2' }}</div
                    >
                    <div>{{ whProductValue ? `${whProductValue.subQtyUnit3}` : '0' }}</div>
                  </div>
                  <div> + </div>
                  <div>
                    <Input
                      :placeholder="
                        whProductValue ?
                          `[${whProductValue['Default Unit']}] ${whProductValue.DefaultUnitDescription || 'Units'}`
                        : 'Quantity'
                      "
                      :inputValue.sync="unitValue"
                      @focusChanged="unitChanged"
                      validator="positiveDecimal,24,4"
                      :isRequired="true"
                      :isDisabled="!whProductValue || viewMode"
                    />
                  </div>

                  <div> = </div>
                  <div style="flex: 1 0 auto; min-width: 20%">
                    <div>Total Qty</div><div style="font-weight: bold">{{ totalUnitValue }}</div>
                  </div>
                </div>
                <Input
                  :placeholder="
                    whProductValue ?
                      `[${whProductValue['Default Unit']}] ${whProductValue.DefaultUnitDescription || 'Units'}`
                    : 'Quantity'
                  "
                  v-else
                  :inputValue.sync="unitValue"
                  validator="positiveDecimal,24,4"
                  :isRequired="true"
                  :isDisabled="!whProductValue || viewMode"
                  :errorMessage.sync="quantityValueError"
                />
                <div
                  class="error-message"
                  v-if="hasCartonConversion(whProductValue) && quantityValueError"
                >
                  {{ quantityValueError }}
                </div>
              </BoxContainer>
              <div class="flex-container mb-1 w-100" v-if="whProductValue">
                <BoxContainer
                  title="Prices From Master"
                  class="flex-container flex-column w-100"
                  style="gap: 2px; align-items: flex-start; align-self: stretch; text-align: left"
                >
                  <div>Cost Price: {{ $currencyFormat(whProductValue.CostPrice) }}</div>
                  <div>Selling Price: {{ $currencyFormat(whProductValue.SellingPrice) }}</div>
                  <!-- <div>Discount: {{ whProductValue.Discount }}%</div> -->
                </BoxContainer>
                <BoxContainer
                  title="Quantity Info"
                  class="flex-container flex-column w-100"
                  style="gap: 2px; align-items: flex-start; align-self: stretch; text-align: left"
                >
                  <div
                    >Quantity In Store:
                    {{ whProductValue && whProductValue.QtyOnHand ? `${whProductValue.QtyOnHand}` : '0' }}</div
                  >
                  <div v-if="whProductValue ? whProductValue.subPackCode1 : false"
                    >Quantity Per
                    {{
                      whProductValue ?
                        `${whProductValue.Sub1UnitDescription ? whProductValue.Sub1UnitDescription : ''} (${whProductValue.subPackCode1})`
                      : 'subPack1'
                    }}: {{ whProductValue ? `${whProductValue.subQtyUnit2}` : '0' }}</div
                  >
                  <div v-if="quantityAssignedToDO"> Quantity Assigned To DO: {{ quantityAssignedToDO }} </div>
                </BoxContainer>
              </div>

              <BoxContainer title="Total Price" style="margin-bottom: 4px">
                <div class="total-price-wrapper">
                  <div class="flex-container" style="align-items: center">
                    <div class="flex-container" style="align-items: center">
                      <div style="flex: 1 0 auto">Total Units: {{ totalUnitValue }}</div>
                      <div>x</div>
                      <Dropdown
                        placeholder="Unit Price"
                        :search.sync="unitPriceValue"
                        :allowFreeText="true"
                        :items="unitPriceList"
                        itemKey="PriceHistoryLabel"
                        @selected="unitPriceSelected"
                        :isRequired="true"
                        validator="money"
                        :isDisabled="!whProductValue || viewMode"
                        :errorMessage.sync="unitPriceValueError"
                      />
                    </div>
                    <div class="row-total">{{ $currencyFormat(totalCostValue) }}</div>
                  </div>
                  <div class="flex-container" style="align-items: center">
                    <div>Discount: </div
                    ><Input
                      placeholder="Discount %"
                      style="max-width: 120px"
                      :inputValue.sync="discountValue"
                      validator="positiveDecimal,3,2"
                      @focusChanged="discountValidator"
                      :isDisabled="viewMode || !whProductValue"
                    />
                    <!-- <div class="row-total">{{
                      totalDiscountValue > 0 ? `- ${$currencyFormat(totalDiscountValue)}` : ''
                    }}</div> -->
                  </div>

                  <div style="border-bottom: 1px solid black; width: 50%" v-if="taxItems.length > 0">Taxes</div>
                  <div class="taxes" v-html="renderTax" style="display: flex; flex-direction: column; gap: 4px"> </div>

                  <div class="flex-container total-row">
                    <div style="margin-left: auto">Net Price (Inclusive Tax)</div>
                    <div class="row-total" style="margin-left: 0; font-weight: bold">{{
                      $currencyFormat(netPriceValue)
                    }}</div>
                  </div>
                </div>
              </BoxContainer>
              <Input
                placeholder="Remarks"
                style="margin-top: 16px"
                :inputValue.sync="productRemarksValue"
                :isDisabled="viewMode"
              />
            </div>
          </div>
          <div class="tab-container" :slot="deliveryListTitle">
            <div class="delivery-info-wrapper">
              <div class="flex-container">
                <Button style="margin-left: auto" @click="deliveryInfoPopupOpen" v-if="!viewMode"
                  >Add Delivery Info</Button
                >
              </div>

              <BoxContainer title="Delivery Info" v-for="(DO, index) in doList" :key="index">
                <div class="flex-container align-items-center">
                  <div class="flex-container" style="width: calc(50% - 4px); align-items: center">
                    <Input placeholder="Send To Location" :inputValue.sync="DO['CompanyName']" :isDisabled="true" />
                  </div>
                  <DateTimePicker
                    placeholder="Require Date"
                    :isDisabled="true"
                    :value="DO['ReqDate'] ? new Date(DO['ReqDate']) : null"
                    :showTime="false"
                  />
                  <Button style="margin-left: auto; margin-top: -6px" @click="doEditClick(DO)" v-if="!viewMode">{{
                    viewMode ? 'View' : 'Edit'
                  }}</Button>
                  <Button style="margin-top: -6px" @click="doDelete(DO)" v-if="!viewMode">Delete</Button>
                </div>
                <div class="flex-container">
                  <div class="w-100">
                    <textarea
                      placeholder="Delivery Address"
                      class="address-text"
                      style="height: 80px"
                      name="delivery-address"
                      :value="DO['Addr']"
                      disabled
                    ></textarea>
                  </div>
                  <div class="w-100">
                    <Input :isDisabled="true" placeholder="Vehice No" :inputValue.sync="DO['VehicleNo']" />
                    <Input :isDisabled="true" placeholder="Driver Name" :inputValue.sync="DO['DriverName']" />
                    <Input :isDisabled="true" placeholder="Driver IC No" :inputValue.sync="DO['DriverIC']" />
                  </div>
                </div>
                <div class="flex-container">
                  <Input placeholder="Contact" class="w-100" :isDisabled="true" :inputValue.sync="DO['Contact']" />
                  <div class="flex-container w-100">
                    <Input placeholder="Phone No" :isDisabled="true" :inputValue.sync="DO['PhoneNo1']" />
                    <Input placeholder="Fax No" :isDisabled="true" :inputValue.sync="DO['PhoneNo2']" />
                  </div>
                </div>
                <TableBox
                  :tableData="filterDOProductList(DO['items'])"
                  v-if="!isFullDelivery(DO['items'])"
                  :hideBottomBlock="true"
                  title="Product List"
                  :hideColumns="['SeqNo', 'ItemTxnID', 'action']"
                  :dataType="doProductListDataType"
                  :columnOrderViews.sync="doProductListColumnOrderViews"
                  :columnOrderID="doProductListColumnOrderID"
                />
                <CheckBox
                  v-if="isFullDelivery(DO['items'])"
                  title="Full Delivery"
                  :isChecked="true"
                  :isDisabled="true"
                  style="margin: 4px"
                />
              </BoxContainer>
              <div v-if="doList.length < 1" class="do-empty-text">
                No Delivery Info Added. Please add delivery info.
              </div>
            </div>
          </div>
        </Tab>
      </div>
    </PopupEntry>
    <div class="delivery-info-popup" v-if="deliveryInfoPopUpShow">
      <div class="delivery-info-popup-inner">
        <BoxContainer title="Delivery Info">
          <div class="flex-container align-items-center">
            <div class="flex-container" style="width: calc(50% - 4px); align-items: center">
              <InputQuery
                placeholder="Send To Location"
                :allowFreeText="true"
                :selectedObject.sync="doLocationValue"
                :search.sync="doLocationInputValue"
                :isRequired="true"
                validator="text,100"
                :errorMessage.sync="doLocationValueError"
                :items="doLocationList"
                itemKey="Company"
                :dataType="doLocationListDataType"
                @selected="doLocationSelected"
                :isDisabled="viewMode"
              />
            </div>
            <DateTimePicker
              placeholder="Require Date"
              :showTime="false"
              :value.sync="doRequireDateValue"
              :isDisabled="viewMode"
            />
          </div>
          <div class="flex-container">
            <div class="w-100">
              <textarea
                placeholder="Delivery Address"
                class="address-text"
                style="height: 80px"
                name="delivery-address"
                v-model="doDeliveryAddressValue"
                :disabled="viewMode"
              ></textarea>
              <div v-if="doDeliveryAddressValueError" class="error-message">{{ doDeliveryAddressValueError }}</div>
            </div>
            <div class="w-100">
              <Dropdown
                placeholder="Vehicle No"
                :allowFreeText="true"
                :search.sync="doVehicleNoValue"
                :items="vehicleNoList"
                validator="text,25"
                itemKey="Inventory #"
                :isDisabled="viewMode"
              />
              <Dropdown
                placeholder="Driver"
                :allowFreeText="true"
                :search.sync="doDriverNoValue"
                :items="employeeList"
                itemKey="Employee Name"
                validator="text,100"
                @selected="driverSelected"
                :isDisabled="viewMode"
              />
              <Input placeholder="IC No" :inputValue.sync="doICNoValue" validator="text,12" :isDisabled="viewMode" />
            </div>
          </div>
          <div class="flex-container">
            <Input placeholder="Contact" class="w-100" :inputValue.sync="doContactValue" :isDisabled="viewMode" />
            <div class="flex-container w-100">
              <Input
                placeholder="Phone No"
                :inputValue.sync="doPhoneNoValue"
                validator="text,20"
                :isDisabled="viewMode"
              />
              <Input placeholder="Fax No" :inputValue.sync="doFaxNoValue" validator="text,20" :isDisabled="viewMode" />
            </div>
          </div>
          <CheckBox
            title="Full Delivery"
            style="margin: 4px"
            v-if="(doFieldsMode == 'N' && doList.length < 1) || (doFieldsMode == 'E' && doList.length <= 1)"
            :isChecked.sync="doFullDeliveryValue"
            :isDisabled="viewMode"
            @checkBoxClicked="selectedDoProduct = null"
          />
          <BoxContainer v-if="!doFullDeliveryValue" title="Product List" class="doProductListContainer">
            <div class="table-tab-container flex-1">
              <div class="table-container">
                <div class="flex-container align-items-center mb-1" v-if="!viewMode">
                  <Button style="margin-left: auto" @click="doProductFieldsOpen" :disabled="selectedDoProduct"
                    >Add</Button
                  >
                  <Button :disabled="doProductSelectedRows.length < 1" @click="doProductDelete">Delete</Button>
                </div>
                <TableBox
                  :tableData="doProductListFiltered"
                  title="Product List"
                  :hideColumns="['SeqNo', 'ItemTxnID']"
                  :selectedRow.sync="doProductSelectedRows"
                  :additionalButtons="tableAdditionalButton"
                  @buttonClicked="doProductListTableClick"
                  :dataType="doProductListDataType"
                  :columnOrderViews.sync="doProductListColumnOrderViews"
                  :columnOrderID="doProductListColumnOrderID"
                />
              </div>
              <div v-if="selectedDoProduct">
                <div class="flex-container align-items-center mb-2 justify-content-end" v-if="!viewMode">
                  <Button style="margin-left: auto" @click="doProductSave">Save</Button>
                  <Button @click="doProductCancelEdit">Cancel</Button>
                </div>
                <div class="flex-container">
                  <Dropdown
                    placeholder="Product"
                    :selectedObject.sync="doProductProductValue"
                    :errorMessage.sync="doProductProductValueError"
                    :items="doProductProductList"
                    itemKey="label"
                    :isRequired="true"
                    :isDisabled="viewMode"
                  />
                  <CheckBox
                    style="flex: 1 0 auto; margin-right: 8px"
                    title="Loose Item"
                    :isChecked.sync="doLooseItemValue"
                    :isDisabled="viewMode"
                  />
                </div>
                <BoxContainer title="Quantity">
                  <div class="flex-container">
                    <Input
                      :inputValue.sync="doProductCartonValue"
                      :placeholder="
                        doProductProductValue ?
                          `[${doProductProductValue.subPackCode2}] ${doProductProductValue.Sub2UnitDescription}`
                        : 'subPack2'
                      "
                      v-if="hasCartonConversion(doProductProductValue)"
                      validator="positiveDecimal,24,4"
                      :isRequired="true"
                      :isDisabled="!doProductProductValue || viewMode"
                    />
                    <Input
                      :inputValue.sync="doProductQuantityValue"
                      :placeholder="
                        doProductProductValue && doProductProductValue['Default Unit'] ?
                          `[${doProductProductValue['Default Unit']}] ${doProductProductValue.DefaultUnitDescription || 'Units'}`
                        : 'Quantity'
                      "
                      validator="positiveDecimal,24,4"
                      :isRequired="true"
                      :isDisabled="!doProductProductValue || viewMode"
                      @focusChanged="doUnitChanged"
                    />
                    <Button v-if="!viewMode" :disabled="!doProductProductValue" @click="doProductAddMax">Max</Button>
                  </div>
                  <div class="flex-container ml-2" v-if="hasCartonConversion(doProductProductValue)">
                    <div>Total Quantity:</div>
                    <div>{{ doProductTotalQuantity }}</div>
                  </div>
                  <div class="error-message" v-if="doProductQuantityValueError">{{ doProductQuantityValueError }}</div>
                </BoxContainer>
                <BoxContainer title="Quantity Info">
                  <div class="ml-2 flex-container flex-column" style="gap: 2px; align-items: flex-start; text-align: left">
                    <div v-if="hasCartonConversion(doProductProductValue)"
                      >Quanity Per {{ doProductProductValue.subPackCode2 }}:
                      {{ doProductProductValue.subQtyUnit3 }}</div
                    >
                    <div
                      >Quantity Left To Add: {{ doProductProductValue ? doProductProductValue.QuantityLeft : '0' }}
                    </div>
                  </div>
                </BoxContainer>
              </div>
            </div>
          </BoxContainer>
        </BoxContainer>
        <div class="flex-container mt-1 justify-content-end">
          <div v-if="doPopupError" class="error-message">{{ doPopupError }}</div>
          <Button style="margin-left: auto" @click="doPopupSave" :disabled="selectedDoProduct" v-if="!viewMode"
            >Save</Button
          >
          <Button @click="doPopupCancel" :disabled="selectedDoProduct && !viewMode">Cancel</Button>
        </div>
      </div>
    </div>

    <PopupV2
      :show="showDeletePopup"
      title="Delete Purchase Order"
      @buttonClicked="deletePopupButtonClicked"
      :buttons="deletePopupButtons"
    >
      <div
        >Are you sure you want to delete Purchase Order
        {{ purchaseOrderTableSelectedRows[0] ? purchaseOrderTableSelectedRows[0]['PO #'] : '' }}?</div
      >

      <Input placeholder="Reason" :inputValue.sync="deleteRemarks" :errorMessage.sync="deleteRemarksError" />
    </PopupV2>
  </div>
</template>

<script>
export default {
  components: {},
  computed: {
    menu() {
      const menuEnabled = !this.purchaseOrderTableLoading;
      let menuItems = [
        {
          title: 'New Purchase Order',
          icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-add-new-50.png',
          key: 'New',
          visible: true,
          enabled: menuEnabled,
          control: 'mnunew',
        },
        {
          title: 'Edit Purchase Order',
          icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-edit-64%20(1).png',
          key: 'Edit',
          visible: true,
          enabled: menuEnabled,
          control: 'mnuEdit',
        },
        {
          title: 'Delete Purchase Order',
          icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-reduce-50.png',
          key: 'Delete',
          visible: true,
          enabled: menuEnabled,
          control: 'mnuDelete',
        },
        {
          title: 'View Purchase Order',
          icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-view-64.png',
          key: 'View',
          visible: true,
          enabled: menuEnabled,
          control: 'mnuFind',
        },
        {
          title: 'Print Purchase Order',
          icon: 'https://dsmlib.logistics.systems/Icons/Print.png',
          key: 'Print',
          visible: true,
          enabled: menuEnabled,
          control: 'mnuInvoice',
        },
      ];

      menuItems = menuItems.map((item) => {
        const { control } = item;
        const SAS = this.SASControl.find((sas) => sas.Control == control) || null;
        if (!SAS) return item;
        const { Properties, PropertiesValue } = SAS;
        return {
          ...item,
          [Properties.toLowerCase()]: PropertiesValue == 'True',
        };
      });

      return menuItems;
    },

    purchaseOrderListFiltered() {
      const reorder = this.purchaseOrderList.map((item, index) => {
        const {
          PONO,
          PODate,
          Supplier,
          Spp_Name,
          Status,
          PartialDelivery,
          JobDescription,
          TotalValue,
          RefNo,
          dateUpdate,
          UserID,
        } =
          item;
        return {
          '#': index + 1,
          'PO #': PONO,
          'Ref #': RefNo,
          'Purchase Date': PODate,
          Supplier: `${Supplier} - ${Spp_Name}`,
          'Job Type': JobDescription ?? '',
          'Total Value': TotalValue,
          Status: this.statusSet[Status] ? `${Status} (${this.statusSet[Status]})` : Status,
          'Partial Delivery': PartialDelivery ?? 'No',
          'Date Updated': dateUpdate,
          'Updated By': UserID,
        };
      });

      return reorder;
    },

    totalAmountValue() {
      if (this.productListFiltered.length > 0) {
        const total = this.productListFiltered.reduce((acc, item) => acc + parseFloat(item['Final Total']), 0);
        return isNaN(total) ? 0 : total;
      }
      return 0;
    },

    partialDeliveryStatus() {
      const deliveredTotal = this.doListRaw
        .filter((item) => item.action != 'D')
        .reduce((acc, item) => acc + parseFloat(item.Qty || 0), 0);
      return deliveredTotal > 0 && deliveredTotal < this.productQuantityTotal ? 'Yes' : 'No';
    },

    quantityAssignedToDO() {
      const { 'Product ID': ItemTxnID } = this.whProductValue || {};
      if (!ItemTxnID) return false;

      const assignedQty = this.doListRaw.reduce((acc, item) => {
        if (item['ItemTxnID'] == ItemTxnID) {
          return acc + parseFloat(item['Qty'] || 0);
        }
        return acc;
      }, 0);

      return assignedQty;
    },

    productListFiltered() {
      const reorder = this.productList
        ?.filter((item) => item.action != 'D')
        .map((item, index) => {
          const {
            ProdCode,
            Name,
            Grade,
            ItemType,
            Quantity,
            QuantityBal,
            UnitPrice,
            Discount,
            GST2TaxCode,
            GST2TaxRate,
            Taxable_AmountGST2,
            ItemTypeIndicator,
            Remark,
            SeqNo,
          } = item || {};

          const taxedAmount = parseFloat(Taxable_AmountGST2 || '0');
          const totalPrice = parseFloat(Quantity) * parseFloat(UnitPrice);
          const netPrice = totalPrice + taxedAmount;
          return {
            '#': index + 1,
            'Product Name': `${ProdCode} - ${Name}` ?? '',
            Grade: Grade ?? '',
            'Item Type': ItemType ?? '',
            Quantity: parseFloat(Quantity).toFixed(4) ?? '',
            'Quantity Bal': parseFloat(QuantityBal || Quantity).toFixed(4) ?? '',
            'Unit Price': this.$currencyFormat(UnitPrice) ?? '',
            Total: totalPrice.toFixed(2) ?? '',
            'Tax Code': GST2TaxCode ?? '',
            'Tax Rate(%)': GST2TaxRate || 0,
            'Taxed Amount': taxedAmount,
            'Final Total': netPrice.toString() ?? '',
            'Discount (%)': parseFloat(Discount).toFixed(2) ?? '',
            'Loose Item': ItemTypeIndicator == '1' ? 'Yes' : ('No' ?? ''),
            Remark: Remark ?? '',
            SeqNo: SeqNo,
          };
        });

      return reorder;
    },

    doList() {
      const addProductInfo = this.doListRaw.map((item) => {
        const { ItemTxnID } = item;
        const findProduct = this.whProductList.find((prod) => prod['Product ID'] == ItemTxnID);
        const { Grade, 'Product Code': ProdCode, Description, 'Item Type': ItemType } = findProduct || {};

        return { ProdCode, ProdDesc: Description, ItemType, Grade, ...item };
      });

      const itemFields = [
        'ItemTxnID',
        'Qty',
        'Grade',
        'ItemTypeIndicator',
        'SeqNo',
        'ProdCode',
        'ProdDesc',
        'ItemType',
        'Grade',
        'UserID',
        'DateUpdated',
        'action',
      ];

      const grouped = Object.values(
        addProductInfo.reduce((acc, item) => {
          // Use unique key (e.g. based on PONo + CompanyName + Addr etc.)
          const key = [
            item.PONo,
            item.CompanyName,
            item.Contact,
            item.Addr,
            item.ReqDate,
            item.PhoneNo1,
            item.PhoneNo2,
            item.VehicleNo,
            item.DriverName,
            item.DriverIC,
          ].join('|');

          if (!acc[key]) {
            // create base group
            acc[key] = {
              PONo: item.PONo,
              CompanyName: item.CompanyName,
              Contact: item.Contact ?? '',
              Addr: item.Addr ?? '',
              ReqDate: item.ReqDate ?? '',
              PhoneNo1: item.PhoneNo1 ?? '',
              PhoneNo2: item.PhoneNo2 ?? '',
              VehicleNo: item.VehicleNo ?? '',
              DriverName: item.DriverName ?? '',
              DriverIC: item.DriverIC ?? '',
              items: [],
            };
          }

          // push item fields only
          acc[key].items.push(Object.fromEntries(itemFields.map((f) => [f, item[f]])));

          return acc;
        }, {}),
      );

      const reOrderItems = grouped.map((group) => {
        const { items } = group;

        const rename = items.map((item, index) => {
          const { ProdCode, Grade, Qty, ItemTypeIndicator, ItemTxnID, SeqNo, action } = item;
          const { Description: ProdDesc, ItemType } =
            this.whProductList.find((prod) => prod['Product ID'] == ItemTxnID) || {};
          return {
            '#': index + 1,
            'Product Code': ProdCode ?? '',
            'Product Name': ProdDesc ?? '',
            'Item Type': ItemType ?? '',
            Grade: Grade ?? '',
            'Loose Item': ItemTypeIndicator == '1' ? 'Yes' : ('No' ?? ''),
            Quantity: parseFloat(Qty).toFixed(4) ?? '',
            ItemTxnID,
            SeqNo,
            action,
          };
        });

        return {
          ...group,
          items: rename,
        };
      });

      const removeEmpty = reOrderItems.filter((item) => item.items.filter((prod) => prod.action != 'D').length > 0);

      return removeEmpty;
    },

    popUpTitle() {
      return `${this.menuMode} Purchase Order ${this.selectedPO ? `- ${this.selectedPO.PONO}` : ''}`;
    },

    productListTitle() {
      const length = this.productListFiltered.length;
      return `Products${length > 0 ? ` (${length})` : ''}`;
    },

    deliveryListTitle() {
      const length = this.doList.length;
      return `Delivery Info${length > 0 ? ` (${length})` : ''}`;
    },

    tabItems() {
      return [{ title: this.productListTitle }, { title: this.deliveryListTitle }];
    },

    tableAdditionalButton() {
      if (this.viewMode) return [{ text: 'View', index: 0, width: 60 }];
      return [{ text: 'Edit', index: 0, width: 60 }];
    },

    totalUnitValue() {
      if (!this.whProductValue) return 0;
      const unitQty = parseInt(this.unitValue) || 0;

      let totalUnits = unitQty;
      if (this.hasCartonConversion(this.whProductValue)) {
        const cartonQty = parseInt(this.cartonValue) || 0;
        const cartonSubUnit = parseInt(this.whProductValue.subQtyUnit3) || 0;
        totalUnits += cartonQty * cartonSubUnit;
      }

      return totalUnits ?? 0;
    },

    totalCostValue() {
      const price = parseFloat(this.unitPriceValue || 0);
      const totalCost = (this.totalUnitValue * price).toFixed(4);
      return totalCost.toString() ?? '';
    },

    // totalDiscountValue() {
    //   if (!this.totalCostValue || !this.discountValue) return '';
    //   let discount = this.discountValue;
    //   if (discount > 100) discount = 100;
    //   const totalDiscount = (this.totalCostValue * discount) / 100;
    //   return `${totalDiscount}`;
    // },

    taxItems() {
      if (!this.whProductValue) return [];
      const taxesArr = [];

      // const { ARTaxCode, ARTaxDesc, ARTaxRate } = this.whProductValue;
      //Disabed Taxes so far not used
      // if (ARTaxCode) {
      //   taxesArr.push({ taxCode: ARTaxCode, taxDesc: ARTaxDesc, taxRate: ARTaxRate });
      // }
      const { APTaxCode, APTaxDesc, APTaxRate, APTaxType } = this.whProductValue;
      if (APTaxCode && APTaxType != 'GST') {
        taxesArr.push({ taxCode: APTaxCode, taxDesc: APTaxDesc, taxRate: APTaxRate });
      }
      return taxesArr;
    },

    renderTax() {
      if (this.taxItems.length < 1) return ``;
      const html = this.taxItems
        .map((tax) => {
          const { taxCode, taxDesc, taxRate } = tax;
          const totalTaxed = this.totalCostValue > 0 ? this.$currencyFormat((this.totalCostValue * taxRate) / 100) : '';
          return `<div class="flex-container" style="display: flex; gap: 8px;">
        <div style="text-wrap: balance">(${taxCode}) ${taxDesc}</div>
        <div class="row-total" style="margin-left: auto; min-width: 10%; padding-left: 4px; text-align:end;"> ${taxRate}%</div>
        <div class="row-total" style="min-width: 20%; padding-left: 4px; text-align:end;">${totalTaxed}</div>
        </div>`;
        })
        .join('');

      return html;
    },

    netPriceValue() {
      if (!this.totalCostValue) return 0;
      // let total = this.totalCostValue - (this.totalCostValue * this.discountValue) / 100;
      let total = this.totalCostValue;
      this.taxItems.map((tax) => {
        total += (total * tax.taxRate) / 100;
      });
      return total;
    },

    productQuantityTotal() {
      const productQuantityTotal = this.productList
        .filter((item) => item.action != 'D')
        .reduce((acc, item) => acc + parseFloat(item?.['Quantity'] || 0), 0);
      return productQuantityTotal;
    },

    whProductListFiltered() {
      // const filterProduct = this.whProductList.filter((item) => {
      //   const { 'Product ID': ItemTxnID } = item;
      //   const findProduct = this.productList.find((prod) => prod['ItemTxnID'] == ItemTxnID);
      //   return !findProduct;
      // });

      // return filterProduct;
      return this.whProductList;
    },

    doProductProductList() {
      let doProductList = [];
      doProductList = Object.values(
        this.productList
          .filter((item) => item.action != 'D')
          .reduce((acc, item) => {
            const { ItemTxnID, Quantity } = item || {};

            if (!acc[ItemTxnID]) {
              acc[ItemTxnID] = {
                ItemTxnID,
                TotalQuantity: 0,
              };
            }

            acc[ItemTxnID].TotalQuantity += parseFloat(Quantity || 0);
            return acc;
          }, {}),
      );

      // add whproduct info
      doProductList = doProductList.map((item) => {
        const { ItemTxnID } = item;
        const findProduct = this.whProductList.find((prod) => prod['Product ID'] == ItemTxnID);
        return { ...item, ...findProduct };
      });

      //get quanity left, subtract added
      doProductList = doProductList.map((item) => {
        const { ItemTxnID, TotalQuantity } = item;
        const findQuantity = this.doListRaw.filter((prod) => prod['ItemTxnID'] == ItemTxnID && prod.action != 'D');
        const totalQtyAssigned = findQuantity.reduce((sum, item) => {
          return sum + parseFloat(item.Qty || 0);
        }, 0);
        const totalQtyLeft = TotalQuantity - totalQtyAssigned;
        return { ...item, QuantityLeft: totalQtyLeft };
      });

      //add back deleted item quantity from doproductlist
      doProductList = doProductList.map((item) => {
        const { ItemTxnID, QuantityLeft } = item;
        const findDoProductDeleted = this.doProductList.filter(
          (prod) => prod['ItemTxnID'] == ItemTxnID && prod.action == 'D',
        );
        if (findDoProductDeleted.length < 1) return item;
        const totalToAddBack = findDoProductDeleted.reduce((sum, item) => {
          return sum + (typeof item.Qty === 'number' ? item.Qty : parseFloat(item.Qty) || 0);
        }, 0);
        return { ...item, QuantityLeft: QuantityLeft + totalToAddBack };
      });

      //filter out
      doProductList = doProductList.filter((item) => {
        const isSameItem = this.selectedDoProduct?.['ItemTxnID'] == item.ItemTxnID;
        return (
          isSameItem ||
          (item.QuantityLeft > 0 &&
            !this.doProductList
              .filter((item) => item.action != 'D')
              .some((prod) => prod['ItemTxnID'] == item.ItemTxnID))
        );
      });

      return doProductList;
    },

    doProductTotalQuantity() {
      const { subQtyUnit3 } = this.doProductProductValue || {};

      const parseQuantity =
        isNaN(parseFloat(this.doProductQuantityValue)) ? 0 : parseFloat(this.doProductQuantityValue);

      let totalQuantity = parseQuantity;
      if (this.hasCartonConversion(this.doProductProductValue)) {
        const parseCartonQuantity =
          isNaN(parseFloat(this.doProductCartonValue)) ? 0 : parseFloat(this.doProductCartonValue);
        totalQuantity += parseCartonQuantity * subQtyUnit3;
      }

      return totalQuantity;
    },

    doProductListFiltered() {
      return this.doProductList
        .filter((item) => item.action != 'D')
        .map((item, index) => {
          const { ProdCode, Grade, Qty, ItemTypeIndicator, ItemTxnID, SeqNo } = item || {};
          const { Description: ProdDesc, 'Item Type': ItemType } =
            this.whProductList.find((prod) => prod['Product ID'] == ItemTxnID) || {};

          return {
            '#': index + 1,
            'Product Code': ProdCode ?? '',
            'Product Name': ProdDesc ?? '',
            'Item Type': ItemType ?? '',
            Grade: Grade ?? '',
            'Loose Item': ItemTypeIndicator == '1' ? 'Yes' : ('No' ?? ''),
            Quantity: parseFloat(Qty).toFixed(4) ?? '',
            ItemTxnID,
            SeqNo,
          };
        });
    },
  },
  data: function () {
    return {
      purchaseOrderList: [],
      purchaseOrderTableLoading: true,
      purchaseOrderTableSelectedRows: [],
      purchaseOrderTableColumnOrderViews: [],
      purchaseOrderTableColumnOrderID: null,

      showPopup: false,
      selectedTabIndex: 0,
      viewMode: false,
      menuMode: '',
      popupEntryRequiredError: [],
      popupEntrySuccessMessages: [],
      popupEntryErrorMessages: [],

      lastUpdatedInfo: '',

      selectedPO: null,

      jobTypeValue: null,
      poDateValue: null,
      refNoValue: '',
      statusValue: '',
      supplierValue: null,
      remarksValue: '',
      remarksValueError: '',

      poDateValueError: '',
      jobTypeValueError: '',
      supplierValueError: '',

      whProductValue: null,
      looseItemValue: false,
      cartonValue: '',
      unitValue: '',

      unitPriceValue: '',
      discountValue: '',
      productRemarksValue: '',

      whProductValueError: '',
      quantityValueError: '',
      unitPriceValueError: '',

      productList: [],
      productListTableSelectedRows: [],
      productListColumnOrderViews: [],
      productListColumnOrderID: null,

      doListRaw: [],

      selectedProduct: null,

      jobTypeList: [],
      supplierList: [],
      whProductList: [],
      unitPriceList: [],

      doProductList: [],
      doProductSelectedRows: [],
      doProductListColumnOrderViews: [],
      doProductListColumnOrderID: null,

      doFieldsMode: 'N',
      selectedDO: null,

      doLocationValue: null,
      doLooseItemValue: false,
      doLocationInputValue: '',
      doDeliveryAddressValue: '',
      doRequireDateValue: null,
      doVehicleNoValue: '',
      doDriverNoValue: '',
      doICNoValue: '',
      doContactValue: '',
      doPhoneNoValue: '',
      doFaxNoValue: '',

      doLocationValueError: '',
      doDeliveryAddressValueError: '',
      doPopupError: '',

      doFullDeliveryValue: false,

      selectedDoProduct: null,

      doProductProductValue: null,
      doProductCartonValue: '',
      doProductQuantityValue: '',

      doProductProductValueError: '',
      doProductQuantityValueError: '',

      doLocationList: [],
      vehicleNoList: [],
      employeeList: [],

      productListDataType: {
        '#': 'number',
        'Product Name': 'string',
        Grade: 'string',
        'Item Type': 'string',
        Quantity: 'number',
        'Quantity Bal': 'number',
        'Unit Price': 'currency',
        Total: 'currency',
        'Tax Code': 'string',
        'Tax Rate(%)': 'number',
        'Taxed Amount': 'currency',
        'Final Total': 'currency',
        'Discount (%)': 'number',
        'Loose Item': 'string',
        Remarks: 'string',
      },

      purchaseOrderListDataType: {
        '#': 'number',
        'PO #': 'string',
        'Ref #': 'string',
        'Purchase Date': 'date',
        Supplier: 'string',
        Status: 'string',
        'Partial Delivery': 'string',
        'Job Type': 'string',
        'Total Value': 'currency',
        'Date Updated': 'datetime',
        'Updated By': 'string',
      },
      whProductListDataType: {
        '#': 'number',
        'Product Code': 'string',
        Description: 'string',
        'Default Unit': 'string',
        'Item Type': 'string',
        'Product Origin': 'string',
        Size: 'string',
        Shade: 'string',
        Remarks: 'string',
      },

      doProductListDataType: {
        '#': 'number',
        'Product Code': 'string',
        'Product Name': 'string',
        'Item Type': 'string',
        Grade: 'string',
        'Loose Item': 'string',
        Quantity: 'number',
      },

      doLocationListDataType: {
        Company: 'string',
        Contact: 'string',
        Addr1: 'string',
        Addr2: 'string',
        Addr3: 'string',
        Addr4: 'string',
        PostCode: 'string',
        City: 'string',
        State: 'string',
        Country: 'string',
        Phone: 'string',
        Fax: 'string',
        VehicleNo: 'string',
        DriverName: 'string',
        DriverIC: 'string',
        ReqDate: 'date',
      },

      supplierDataType: {
        '#': 'number',
        'Supplier Code': 'string',
        'Short Name': 'string',
        'Supplier Name': 'string',
        Category: 'string',
        Address: 'string',
        PostCode: 'number',
        City: 'string',
        State: 'string',
        Country: 'string',
        Contact: 'string',
        'Tel No': 'string',
        'Fax No': 'string',
        BrCode: 'string',
        cocode: 'string',
        'Last Update Time': 'datetime',
        'Last Update UserID': 'string',
        'Create Time': 'string',
        'Create UserID': 'string',
      },

      statusSet: {
        N: 'New',
        P: 'Printed',
        C: 'Cancelled',
        S: 'Send',
        H: 'HQ read PO/SR',
      },

      deliveryInfoPopUpShow: false,

      showDeletePopup: false,
      deleteRemarks: '',
      deleteRemarksError: '',

      deletePopupButtons: [
        { text: 'Delete', color: 'red', textColor: 'white' },
        { text: 'Cancel', color: 'lightgray', textColor: 'black' },
      ],

      SASControl: [],
    };
  },
  props: {},
  methods: {
    async unitPriceSelected(selected) {
      if (!selected) return;
      await this.$nextTick();
      this.unitPriceValue = selected.Price;
    },
    hasCartonConversion(product) {
      return product?.Sub == '1' && Number(product.subQtyUnit3) > 0;
    },
    menuClicked(e) {
      this.viewMode = false;
      this.menuMode = e.key;
      this.selectedTabIndex = 0;
      this.selectedPO = null;
      switch (e.key) {
        case 'New': {
          this.showPopup = true;
          this.viewMode = false;
          this.openPopup();
          break;
        }

        case 'Edit': {
          if (this.purchaseOrderTableSelectedRows.length < 1) {
            this.$showAlert('error', 'No Purchase Order is selected', 'Please select a Purchase Order to Edit');
            break;
          }
          const { 'PO #': PONo } = this.purchaseOrderTableSelectedRows[0];
          this.openPopup();
          this.populatePopupFields(PONo);
          this.showPopup = true;
          break;
        }
        case 'View': {
          if (this.purchaseOrderTableSelectedRows.length < 1) {
            this.$showAlert('error', 'No Purchase Order is selected', 'Please select a Purchase Order to View');
            break;
          }
          const { 'PO #': PONo } = this.purchaseOrderTableSelectedRows[0];
          this.openPopup();
          this.populatePopupFields(PONo);
          this.showPopup = true;
          this.viewMode = true;
          break;
        }
        case 'Delete': {
          if (this.purchaseOrderTableSelectedRows.length < 1) {
            this.$showAlert('error', 'No Purchase Order is selected', 'Please select a Purchase Order to Delete');
            break;
          }
          this.deleteRemarks = '';
          this.showDeletePopup = true;
          break;
        }
        case 'Print': {
          let routeString = `/Home/${this.$route.params.user}/FinPayment/PrintPurchaseOrder`;
          if (this.purchaseOrderTableSelectedRows.length > 0) {
            routeString += `?pono=${this.purchaseOrderTableSelectedRows[0]['PO #']}`;
          }
          const print = this.$router.resolve(routeString);

          window.open(print.href, '_blank');
          break;
        }
      }
    },

    async tabClick(_tabItem, tabIndex) {
      if (this.selectedProduct) {
        const discard = await this.warnUnsavedChanges();
        if (!discard) return;
      }

      this.selectedTabIndex = tabIndex;
      this.selectedProduct = null;
      this.selectedDoProduct = null;
    },

    async getJobTypeList() {
      const result = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'PURCHASEORDERJOBTYPE' });
      this.jobTypeList = result;
    },

    async getSupplierList() {
      const result = (await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'SUPPLIER' })).map((item) => ({
        ...item,
        label: `${item['Supplier Code']} - ${item['Supplier Name']}`,
      }));
      this.supplierList = result;
    },

    async getWhProduct() {
      const result = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'WHPRODUCT' });
      this.whProductList = result.map((item) => ({
        ...item,
        label: `${item['Product Code']} - ${item['Description']}`,
      }));
    },
    async getLocationList() {
      const result = await this.$apiCallV2('/PurchaseOrder/GetDeliveryLocation', {});
      this.doLocationList = result;
    },
    async getVehicleList() {
      const result = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'VEHICLE' });
      this.vehicleNoList = result;
    },
    async getEmployeeList() {
      const result = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'EMPLOYEE' });
      this.employeeList = result;
    },

    async getPurchaseOrderProductColumnOrders() {
      const tableConfigs = [
        {
          viewsKey: 'productListColumnOrderViews',
          idKey: 'productListColumnOrderID',
          formctrlname: 'Purchase Order Products',
        },
        {
          viewsKey: 'doProductListColumnOrderViews',
          idKey: 'doProductListColumnOrderID',
          formctrlname: 'Purchase Order Delivery Products',
        },
      ];

      await Promise.all(
        tableConfigs.map(async ({ viewsKey, idKey, formctrlname }) => {
          if (this[idKey] !== null) return;

          const columnOrderID = {
            formname: 'FINPAYMENT_PurchaseOrder',
            formctrlname,
          };
          this[viewsKey] = await this.$apiCallV2('/TableBox/GetColumnOrder', columnOrderID);
          this[idKey] = columnOrderID;
        }),
      );
    },

    async getPurchaseOrderList() {
      this.purchaseOrderTableLoading = true;
      this.purchaseOrderList = [];
      await this.$nextTick();

      const result = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'PURCHASEORDER' });

      const columnOrderID = {
        formname: 'FINPAYMENT_PurchaseOrder',
        formctrlname: 'Purchase Order',
      };
      if (this.purchaseOrderTableColumnOrderID === null) {
        this.purchaseOrderTableColumnOrderViews = await this.$apiCallV2('/TableBox/GetColumnOrder', columnOrderID);
        this.purchaseOrderTableColumnOrderID = columnOrderID;
      }

      this.purchaseOrderList = result;
      this.purchaseOrderTableLoading = false;
    },

    async getPurchaseOrderDetails(PONo) {
      this.$fullscreenLoading('Getting Purchase Order Details...');
      const result = await this.$apiCallV2('/Master/GetDetailsMaster', { masterfile: 'PURCHASEORDER', recordid: PONo });
      this.$fullscreenLoading();
      return result.PO;
    },

    clearPopupFields() {
      this.jobTypeValue = null;
      this.poDateValue = null;
      this.refNoValue = '';
      this.statusValue = 'N (New)';
      this.supplierValue = null;
      this.remarksValue = '';

      this.clearPopupError();

      this.productList = [];
      this.selectedProduct = null;

      this.clearProductFields();

      this.doListRaw = [];
      this.clearDOPopupFields();

      this.clearPopupError();
    },

    clearPopupError() {
      this.popupEntryErrorMessages = [];
      this.popupEntrySuccessMessages = [];
      this.popupEntryRequiredError = [];

      this.poDateValueError = '';
      this.jobTypeValueError = '';
      this.supplierValueError = '';
      this.remarksValueError = '';
    },

    clearProductFields() {
      this.whProductValue = null;

      this.clearWhProductFields();

      this.productRemarksValue = '';
    },

    clearWhProductFields() {
      this.cartonValue = '';
      this.unitValue = '';
      this.unitPriceValue = '';
      this.unitPriceList = [];
      this.discountValue = '';

      this.clearWhProductErrors();
    },

    clearWhProductErrors() {
      this.whProductValueError = '';
      this.quantityValueError = '';
      this.unitPriceValueError = '';
    },

    openPopup() {
      this.clearPopupFields();
      this.showPopup = true;
    },

    async populatePopupFields(PONo) {
      const fetchAPIPO = await this.getPurchaseOrderDetails(PONo);
      this.selectedPO = fetchAPIPO;
      const { RefNo, Status, Supplier, JobConfig, PODate, Remark, PODetail, PODODetail, dateUpdate, UserID } =
        fetchAPIPO;

      const PODetailArray = this.convertToArray(PODetail);
      const DODetailArray = this.convertToArray(PODODetail);

      this.jobTypeValue = this.jobTypeList.find((item) => item.JobTypeID == JobConfig);
      this.poDateValue = PODate ? new Date(PODate) : null;
      this.refNoValue = RefNo;
      this.statusValue = this.statusSet[Status] ? `${Status} (${this.statusSet[Status]})` : Status;
      this.supplierValue = this.supplierList.find((item) => item['Supplier Code'] == Supplier);
      this.remarksValue = Remark;

      await this.getPurchaseOrderProductColumnOrders();

      this.productList = [];
      this.doListRaw = [];
      await this.$nextTick();
      this.productList = PODetailArray;
      this.doListRaw = DODetailArray.map((item) => {
        const { ReqDate } = item;
        const convertReqDate = ReqDate ? new Date(ReqDate).formatString('yyyy-MM-dd') : null;
        return { ...item, ReqDate: convertReqDate };
      });

      this.lastUpdatedInfo = `${UserID} ${new Date(dateUpdate).formatString('dd/MM/yyyy HH:mm')}`;
    },

    openProductFields() {
      this.selectedProduct = {};
      this.productListTableSelectedRows = [];
      this.clearProductFields();
    },

    async populateProductFields(item) {
      this.selectedProduct = item;

      const { ItemTxnID, Quantity, UnitPrice, Discount, ItemTypeIndicator, Remark } = item;

      this.whProductValue = this.whProductList.find((item) => item['Product ID'] == ItemTxnID);
      await this.whProductValueChanged(this.whProductValue);
      this.looseItemValue = ItemTypeIndicator == '1';

      this.unitValue = Quantity.toString();
      this.unitChanged();

      this.unitPriceValue = this.$currencyFormat(UnitPrice);
      this.discountValue = parseFloat(Discount).toFixed(4).toString() || '0';
      this.productRemarksValue = Remark;
    },

    popupEntryRequiredClicked: function (item) {
      this.$refs[item].setFocus();
    },

    async productListTableButtonClick(_text, e) {
      if (this.selectedProduct) {
        const discard = await this.warnUnsavedChanges();
        if (!discard) return;
      }

      const findProduct = this.productList.find((item) => item.SeqNo == e.SeqNo && item.action != 'D');
      this.openProductFields();
      const findFiltered = this.productListFiltered.find((item) => item.SeqNo == e.SeqNo);
      const findIndex = this.productListFiltered.findIndex((item) => item.SeqNo == e.SeqNo);
      this.productListTableSelectedRows = findFiltered ? [{ ...findFiltered, _rowid: `row${findIndex}` }] : [];
      this.populateProductFields(findProduct);
    },

    async whProductValueChanged(selected) {
      this.clearWhProductFields();
      if (!selected) {
        return;
      }
      const { 'Product Code': ProdCode, Discount } = selected;
      const productID = selected['Product ID'];
      this.$fullscreenLoading('Getting Purchase History...');
      try {
        const result = await this.$apiCallV2('/PurchaseOrder/CheckPOPriceHistory', { prodcode: ProdCode });
        if (this.whProductValue?.['Product ID'] != productID) return;

        const pricesByValue = new Map();
        (result || []).forEach((item) => {
          const price = item.Price;
          if (price === null || price === undefined || String(price).trim() === '') return;
          const numericPrice = Number(String(price).replace(/,/g, ''));
          if (!Number.isFinite(numericPrice)) return;
          const formattedPrice = this.$currencyFormat(numericPrice);
          const poDate = item['PO Date'] ? new Date(item['PO Date']) : null;
          const poDateTimestamp = poDate && Number.isFinite(poDate.getTime()) ? poDate.getTime() : null;
          const existing = pricesByValue.get(formattedPrice);
          if (existing && (poDateTimestamp === null || (existing.poDateTimestamp !== null && existing.poDateTimestamp >= poDateTimestamp))) return;

          const formattedDate = poDateTimestamp !== null ? poDate.formatString('dd/MM/yyyy') : '';
          pricesByValue.set(formattedPrice, {
            Price: formattedPrice,
            PriceHistoryLabel: formattedDate ? `${formattedPrice} (${formattedDate})` : formattedPrice,
            poDateTimestamp,
          });
        });

        this.unitPriceList = Array.from(pricesByValue.values())
          .sort((left, right) => (right.poDateTimestamp ?? 0) - (left.poDateTimestamp ?? 0))
          .map(({ Price, PriceHistoryLabel }) => ({ Price, PriceHistoryLabel }));
        this.discountValue = parseFloat(Discount).toFixed(4).toString() || '0';
      } finally {
        this.$fullscreenLoading();
      }
    },

    unitChanged() {
      if (!this.hasCartonConversion(this.whProductValue)) return;

      const inputValue = parseFloat(this.unitValue);
      const subQtyValue = parseFloat(this.whProductValue.subQtyUnit3);
      if (inputValue >= subQtyValue && subQtyValue > 0) {
        this.unitValue = (inputValue % subQtyValue).toString();
        const cartonValue = parseFloat(this.cartonValue);
        const parseCartonValue = isNaN(cartonValue) ? 0 : cartonValue;
        this.cartonValue = (parseCartonValue + Math.floor(inputValue / subQtyValue)).toString();
      }
    },

    productSave() {
      let error = false;

      if (!this.whProductValue) {
        this.whProductValueError = 'Product is required';
        error = true;
      }

      const getSameItemTxnIDSum =
        this.productList
          .filter(
            (item) => item.ItemTxnID == this.whProductValue['Product ID'] && item.SeqNo != this.selectedProduct.SeqNo,
          )
          .reduce((acc, item) => acc + parseFloat(item.Quantity), 0) + parseFloat(this.totalUnitValue);
      if (!this.totalUnitValue || this.totalUnitValue <= 0) {
        this.quantityValueError = 'Quantity is required';
        error = true;
      } else if (this.selectedProduct.SeqNo && getSameItemTxnIDSum < this.quantityAssignedToDO) {
        this.quantityValueError = `Quantity cannot be less than quantity assigned to DO`;
        error = true;
      }

      if (!this.unitPriceValue) {
        this.unitPriceValueError = 'Unit Price is required';
        error = true;
      }

      if (error) return;

      const {
        'Product ID': ProdTxnID,
        Description,
        Grade,
        'Item Type': ItemType,
        ItemTypeKey,
        APTaxCode,
        APTaxRate,
        'Product Code': ProdCode,
        subQtyUnit2,
      } = this.whProductValue;

      const totalPrice = parseFloat(this.totalUnitValue) * parseFloat(this.unitPriceValue);

      const save = {
        Name: Description ?? '',
        Grade: Grade ?? '',
        ItemType: ItemType,
        ItemTypeKey: ItemTypeKey ?? '',
        Quantity: this.totalUnitValue,
        UnitPrice: this.unitPriceValue,
        Discount: this.discountValue,
        GST2TaxCode: APTaxCode,
        GST2TaxRate: APTaxRate,
        Taxable_AmountGST2: (totalPrice * (APTaxRate ? parseFloat(APTaxRate) : 0)) / 100,
        ItemTypeIndicator: this.looseItemValue ? '1' : '0',
        Remark: this.productRemarksValue,

        ItemTxnID: ProdTxnID,

        PONo: this.selectedPO?.PONO || '',
        ProdCode: ProdCode,
        subQtyUnit2: subQtyUnit2,
      };

      let updateProductList = [...this.productList];

      const { SeqNo: selectedSeqNo } = this.selectedProduct || {};
      if (selectedSeqNo) {
        updateProductList = updateProductList.map((item) => {
          if (item.SeqNo == selectedSeqNo) {
            return { ...item, ...save, action: item.action || 'E' };
          }
          return item;
        });
      } else {
        const maxSeqNo = Math.max(0, ...updateProductList.map((item) => Number(item.SeqNo) || 0)) + 1;
        updateProductList.push({ ...save, SeqNo: maxSeqNo, action: 'N' });
      }

      this.productList = [];
      this.$nextTick(() => {
        this.productList = this.convertToArray(updateProductList);
      });

      this.selectedProduct = null;
    },

    async productDelete() {
      const { SeqNo } = this.productListTableSelectedRows[0] || {};
      const findProduct = this.productList.find((item) => item.SeqNo == SeqNo && item.action != 'D');
      const { ItemTxnID, Quantity, action } = findProduct;
      const findInDO = this.doListRaw
        .filter((item) => item.action != 'D')
        .filter((item) => item.ItemTxnID == ItemTxnID);

      const totalSameItemTxnIDSum = this.productList
        .filter((item) => item.ItemTxnID == ItemTxnID && item.action != 'D')
        .reduce((acc, item) => acc + parseFloat(item.Quantity), 0);
      const totalDOQuantity = findInDO.reduce((acc, item) => acc + parseFloat(item.Qty), 0);

      if (totalSameItemTxnIDSum - parseFloat(Quantity) < totalDOQuantity && findInDO.length > 0) {
        const companyNames = Array.from(
          new Set(findInDO.map((item) => item.CompanyName.toString()).filter((name) => name)),
        );
        const doListString = companyNames.map((item) => `<li>${item}</li>`).join('');
        const doErrorString = `<div style="padding: 0px 24px;"><div style="text-align: left;">Please remove all entries of this product from all attached Delivery Info.</div><div style="text-align: left; margin-bottom: 4px;">This Product are assigned to the following Delivery Info:</div><ol style="text-align: left">${doListString}</ol></div>`;
        this.$showAlert('warning', 'You cannot delete a product that is already assigned to a DO', doErrorString);
        return;
      }

      const discard = await this.$showAlert('warning', 'Are you sure you want to delete this Product?', '', [
        { text: 'Delete', color: 'Red', textColor: 'white' },
        { text: 'Cancel' },
      ]);
      if (discard == 'Cancel') return false;

      let update = this.productList;
      if (action == 'N') update = update.filter((item) => !(item.SeqNo == SeqNo && item.action == 'N'));
      else
        update = update.map((item) => {
          if (item.SeqNo == SeqNo) return { ...item, action: 'D' };
          return item;
        });

      this.productListTableSelectedRows = [];
      this.productList = [];
      this.$nextTick(() => {
        this.productList = this.convertToArray(update);
      });
    },

    clearDOPopupFields() {
      this.doFieldsMode = 'N';

      this.doLocationValue = null;
      this.doLocationInputValue = '';
      this.doDeliveryAddressValue = '';
      this.doRequireDateValue = null;
      this.doVehicleNoValue = '';
      this.doDriverNoValue = '';
      this.doICNoValue = '';
      this.doContactValue = '';
      this.doPhoneNoValue = '';
      this.doFaxNoValue = '';

      this.doFullDeliveryValue = false;

      this.clearDOPopupErrors();

      this.doProductList = [];

      this.selectedDoProduct = null;
      this.clearDOproductFields();
    },

    clearDOPopupErrors() {
      this.doLocationValueError = '';
      this.doDeliveryAddressValueError = '';
      this.doPopupError = '';
    },

    deliveryInfoPopupOpen() {
      this.clearDOPopupFields();

      this.deliveryInfoPopUpShow = true;
    },

    doEditClick(DO) {
      this.deliveryInfoPopupOpen();
      this.populateDOPopupFields(DO);
    },

    async populateDOPopupFields(DO) {
      this.doFieldsMode = 'E';
      this.selectedDO = DO;
      const {
        Addr,
        CompanyName,
        Contact,
        DriverIC,
        DriverName,
        PhoneNo1,
        PhoneNo2,
        ReqDate,
        VehicleNo,
        items: productItems,
      } = DO;
      this.doLocationInputValue = CompanyName;
      this.doDeliveryAddressValue = Addr;
      this.doRequireDateValue = ReqDate ? new Date(ReqDate) : null;
      this.doVehicleNoValue = VehicleNo;
      this.doDriverNoValue = DriverName;
      this.doICNoValue = DriverIC;
      this.doContactValue = Contact;
      this.doPhoneNoValue = PhoneNo1;
      this.doFaxNoValue = PhoneNo2;

      this.doFullDeliveryValue = this.isFullDelivery(productItems);

      await this.getPurchaseOrderProductColumnOrders();
      this.doProductList = [];
      await this.$nextTick();
      this.doProductList = productItems.map((item) => {
        const findDoRaw = this.doListRaw.find((raw) => raw.SeqNo == item.SeqNo);
        return findDoRaw;
      });
    },

    async doProductListTableClick(_text, e) {
      if (this.selectedDoProduct) {
        const discard = await this.warnUnsavedChanges();
        if (!discard) return;
      }
      const findDoProduct = this.doListRaw.find((item) => item.SeqNo == e.SeqNo);
      this.populateDOProductFields(findDoProduct);
    },

    clearDOproductFields() {
      this.doProductProductValue = null;
      this.doLooseItemValue = false;
      this.doProductCartonValue = '';
      this.doProductQuantityValue = '';

      this.selectedDoProduct = null;

      this.clearDOProductErrors();
    },

    async doDelete(DO) {
      const discard = await this.$showAlert('warning', 'Are you sure you want to delete this Delivery Info?', '', [
        { text: 'Delete', color: 'Red', textColor: 'white' },
        { text: 'Cancel' },
      ]);
      if (discard == 'Cancel') return false;

      const { items } = DO;
      let updateDORaw = this.doListRaw;
      items.map((item) => {
        const findRaw = updateDORaw.find((raw) => raw.SeqNo == item.SeqNo);
        if (findRaw) {
          const { action } = findRaw;
          if (action == 'N') {
            updateDORaw = updateDORaw.filter((raw) => !(raw.SeqNo == item.SeqNo && raw.action == 'N'));
          } else {
            updateDORaw = updateDORaw.map((raw) => {
              if (raw.SeqNo == item.SeqNo) return { ...raw, action: 'D' };
              return raw;
            });
          }
        }
      });

      this.doListRaw = [];
      this.$nextTick(() => {
        this.doListRaw = this.convertToArray(updateDORaw);
      });
    },

    clearDOProductErrors() {
      this.doProductProductValueError = '';
      this.doProductQuantityValueError = '';
    },

    populateDOProductFields(product) {
      this.clearDOproductFields();
      this.selectedDoProduct = product;
      const { ItemTxnID, Qty, ItemTypeIndicator } = product;
      const findWhProduct = this.doProductProductList.find((item) => item['Product ID'] == ItemTxnID);
      this.doProductProductValue = findWhProduct;
      this.doLooseItemValue = ItemTypeIndicator == '1';
      this.doProductQuantityValue = parseFloat(Qty).toFixed(4).toString();
      this.doUnitChanged();
    },

    doUnitChanged() {
      if (!this.hasCartonConversion(this.doProductProductValue)) return;

      const inputValue = parseFloat(this.doProductQuantityValue);
      const subQtyValue = parseFloat(this.doProductProductValue.subQtyUnit3);
      if (subQtyValue > 0 && inputValue >= subQtyValue) {
        this.doProductQuantityValue = (inputValue % subQtyValue).toString();
        const cartonValue = parseFloat(this.doProductCartonValue);
        const parseCartonValue = isNaN(cartonValue) ? 0 : cartonValue;
        this.doProductCartonValue = (parseCartonValue + Math.floor(inputValue / subQtyValue)).toString();
      }
    },

    doProductFieldsOpen() {
      this.clearDOproductFields();
      this.selectedDoProduct = {};
    },

    doProductAddMax() {
      const parseRemaining =
        isNaN(parseFloat(this.doProductProductValue.QuantityLeft)) ? 0 : (
          parseFloat(this.doProductProductValue.QuantityLeft)
        );

      let returnValue = parseRemaining;
      if (this.selectedDoProduct) {
        const { Qty } = this.selectedDoProduct;
        const parseCurrentQuantity = isNaN(parseFloat(Qty)) ? 0 : parseFloat(Qty);
        returnValue += parseCurrentQuantity;
      }
      this.doProductCartonValue = '0';
      this.doProductQuantityValue = returnValue.toFixed(4).toString();
      this.doUnitChanged();
    },

    doProductCancelEdit() {
      this.selectedDoProduct = null;
      this.clearDOproductFields();
    },

    doLocationSelected() {
      const {
        Contact,
        Addr1,
        Addr2,
        Addr3,
        Addr4,
        PostCode,
        City,
        State,
        Country,
        Phone,
        Fax,
        VehicleNo,
        DriverName,
        DriverIC,
        ReqDate,
      } = this.doLocationValue || {};

      this.doDeliveryAddressValue = [
        Addr1,
        Addr2,
        Addr3,
        Addr4,
        `${PostCode || ''} ${City || ''}`.trim(),
        `${State ? State + ', ' : ''}${Country || ''}`.trim(),
      ]
        .filter((line) => line && line.trim() !== '') // remove empty lines
        .join('\n');
      this.doContactValue = Contact ?? '';
      this.doPhoneNoValue = Phone ?? '';
      this.doFaxNoValue = Fax ?? '';
      this.doVehicleNoValue = VehicleNo ?? this.doVehicleNoValue;
      this.doDriverNoValue = DriverName ?? this.doDriverNoValue;
      this.doICNoValue = DriverIC ?? this.doICNoValue;
      this.doRequireDateValue = ReqDate ? new Date(ReqDate) : null;
    },

    driverSelected(newSelectedObject) {
      const { NRIC } = newSelectedObject || {};
      this.doICNoValue = NRIC ?? '';
    },

    doProductSave() {
      let error = false;
      if (!this.doProductProductValue) {
        this.doProductProductValueError = 'Product is required!';
        error = true;
      }
      if (error) return;

      if (!this.doProductTotalQuantity) {
        this.doProductQuantityValueError = 'Quantity is required!';
        error = true;
      } else if (this.doProductTotalQuantity <= 0) {
        this.doProductQuantityValueError = 'Quantity must be greater than 0!';
        error = true;
      }

      if (error) return;

      const { ItemTxnID, 'Product Code': ProdCode, Grade } = this.doProductProductValue || {};

      const save = {
        PONo: this.selectedPO?.PONO || '',
        CompanyName: this.doLocationInputValue,
        Contact: this.doContactValue ?? '',
        Addr: this.doDeliveryAddressValue ?? '',
        PhoneNo1: this.doPhoneNoValue ?? '',
        PhoneNo2: this.doFaxNoValue ?? '',
        VehicleNo: this.doVehicleNoValue ?? '',
        DriverName: this.doDriverNoValue ?? '',
        DriverIC: this.doICNoValue ?? '',
        ItemTxnID: ItemTxnID,
        ProdCode: ProdCode,
        Grade: Grade,
        Qty: this.doProductTotalQuantity,
        ItemTypeIndicator: this.doLooseItemValue ? '1' : '0',
        ReqDate: this.doRequireDateValue ? new Date(this.doRequireDateValue).formatString('yyyy-MM-dd') : null,
      };

      let updateDoProductList = this.doProductList;

      const { SeqNo, action } = this.selectedDoProduct || {};
      if (SeqNo) {
        updateDoProductList = updateDoProductList.map((item) =>
          item.SeqNo == SeqNo ? { ...item, ...save, action: action ?? 'E' } : item,
        );
      } else {
        const maxSeqNoRaw = this.doListRaw.reduce((acc, item) => Math.max(acc, item.SeqNo), 0) + 1;
        const maxSeqNoList = this.doProductList.reduce((acc, item) => Math.max(acc, item.SeqNo), 0) + 1;
        const maxSeqNo = Math.max(maxSeqNoRaw, maxSeqNoList);
        updateDoProductList.push({ ...save, SeqNo: maxSeqNo, action: action ?? 'N' });
      }

      this.doProductList = [];
      this.$nextTick(() => {
        this.doProductList = updateDoProductList;
      });

      this.selectedDoProduct = null;
    },

    async doProductDelete() {
      const { SeqNo } = this.doProductSelectedRows[0] || {};
      const discard = await this.$showAlert('warning', 'Are you sure you want to delete this Product?', '', [
        { text: 'Delete', color: 'Red', textColor: 'white' },
        { text: 'Cancel' },
      ]);
      if (discard == 'Cancel') return false;

      let update = this.doProductList;

      update = update.map((item) => {
        if (item.SeqNo == SeqNo) return { ...item, action: 'D' };
        return item;
      });

      this.doProductSelectedRows = [];
      this.doProductList = [];
      this.$nextTick(() => {
        this.doProductList = this.convertToArray(update);
      });
    },

    doProductQuantityChange() {
      const parseRemaining =
        isNaN(parseFloat(this.doProductProductValue?.QuantityLeft)) ? 0 : (
          parseFloat(this.doProductProductValue.QuantityLeft)
        );

      let maxQty = parseRemaining;
      if (this.selectedDoProduct) {
        const { Qty } = this.selectedDoProduct;
        const parseCurrentQuantity = isNaN(parseFloat(Qty)) ? 0 : parseFloat(Qty);
        maxQty += parseCurrentQuantity;
      }

      if (this.doProductTotalQuantity > maxQty) {
        this.$nextTick(() => {
          this.doProductAddMax();
        });
      }
    },

    async doPopupCancel() {
      if (this.selectedDoProduct) {
        const discard = await this.warnUnsavedChanges();
        if (!discard) return;
      }
      this.deliveryInfoPopUpShow = false;
    },

    async doPopupSave() {
      if (this.selectedDoProduct) {
        const discard = await this.warnUnsavedChanges();
        if (!discard) return;
      }

      let error = false;
      if (!this.doLocationInputValue) {
        this.doLocationValueError = 'Send To Location is required!';
        error = true;
      }

      if (!this.doDeliveryAddressValue) {
        this.doDeliveryAddressValueError = 'Delivery Address is required!';
        error = true;
      }

      if (this.doProductList.length < 1 && this.doFullDeliveryValue == false) {
        this.doPopupError = 'Product List cannot be empty!';
        error = true;
      }

      if (error) return;

      let updateDORaw = this.doListRaw;

      if (this.doFullDeliveryValue) {
        const uniqueItemTxnID = [...new Set(this.productList.map((item) => item.ItemTxnID.toString()))];

        const addFull = uniqueItemTxnID.map((itemTxnId) => {
          const findItemTxnIDQty = this.productList
            .filter((item) => item.action != 'D')
            .reduce((sum, item) => sum + (item.ItemTxnID == itemTxnId ? parseFloat(item.Quantity || 0) : 0), 0);

          const { 'Product Code': ProdCode } = this.whProductList.find((item) => item['Product ID'] == itemTxnId);

          return {
            PONo: this.selectedPO?.PONO || '',
            CompanyName: this.doLocationInputValue,
            Contact: this.doContactValue ?? '',
            Addr: this.doDeliveryAddressValue ?? '',
            PhoneNo1: this.doPhoneNoValue ?? '',
            PhoneNo2: this.doFaxNoValue ?? '',
            VehicleNo: this.doVehicleNoValue ?? '',
            DriverName: this.doDriverNoValue ?? '',
            DriverIC: this.doICNoValue ?? '',
            ItemTxnID: itemTxnId,
            Qty: findItemTxnIDQty,
            ItemTypeIndicator: '0',
            ReqDate: this.doRequireDateValue ? new Date(this.doRequireDateValue).formatString('yyyy-MM-dd') : null,
            ProdCode: ProdCode,
          };
        });

        let maxSeqNo = updateDORaw.reduce((acc, raw) => Math.max(acc, Number(raw.SeqNo)), 0);
        updateDORaw = addFull.map((item) => {
          const findExist = updateDORaw.find((raw) => raw.ItemTxnID == item.ItemTxnID);
          if (findExist) {
            return { ...findExist, Qty: item.Qty, action: findExist.action == 'N' ? 'N' : 'E' };
          }
          maxSeqNo += 1;
          return { ...item, action: 'N', SeqNo: maxSeqNo };
        });
      } else {
        this.doProductList.map((item) => {
          const { action, SeqNo } = item;
          const findExist = updateDORaw.find((raw) => raw.SeqNo == SeqNo);
          if (findExist) {
            if (action == 'D') {
              updateDORaw = updateDORaw.filter((raw) => !(raw.SeqNo == SeqNo && raw.action == 'N'));
              updateDORaw = updateDORaw.map((raw) => {
                if (raw.SeqNo == SeqNo) return { ...raw, action: 'D' };
                return raw;
              });
            } else
              updateDORaw = updateDORaw.map((raw) => {
                if (raw.SeqNo == SeqNo) {
                  return { ...raw, ...item, action: action == 'N' ? 'N' : 'E' };
                }
                return raw;
              });
          } else if (!findExist) {
            updateDORaw.push({ ...item, action: 'N' });
          }
        });
      }

      updateDORaw = updateDORaw.map((item) => {
        const findInList = this.doProductList.find((prod) => prod.SeqNo == item.SeqNo);
        if (findInList) {
          return {
            ...item,
            PONo: this.selectedPO?.PONO || '',
            CompanyName: this.doLocationInputValue,
            Contact: this.doContactValue ?? '',
            Addr: this.doDeliveryAddressValue ?? '',
            PhoneNo1: this.doPhoneNoValue ?? '',
            PhoneNo2: this.doFaxNoValue ?? '',
            VehicleNo: this.doVehicleNoValue ?? '',
            DriverName: this.doDriverNoValue ?? '',
            DriverIC: this.doICNoValue ?? '',
            ReqDate: this.doRequireDateValue ? new Date(this.doRequireDateValue).formatString('yyyy-MM-dd') : null,
            action: item.action ? item.action : 'E',
          };
        }
        return item;
      });

      this.doListRaw = [];
      this.$nextTick(() => {
        this.doListRaw = updateDORaw;
      });

      this.deliveryInfoPopUpShow = false;
    },

    async popupSave() {
      if (this.selectedProduct) {
        const discard = await this.warnUnsavedChanges();
        if (!discard) return;
        this.selectedProduct = null;
        this.clearProductFields();
      }

      const action =
        this.menuMode == 'Delete' ? 'D'
        : this.menuMode == 'Edit' ? 'E'
        : 'N';

      this.clearPopupError();

      const error = [];
      if (!this.poDateValue) {
        this.poDateValueError = 'Date is required!';
        error.push({ key: 'PODate', value: this.poDateValueError });
      }
      if (!this.jobTypeValue) {
        this.jobTypeValueError = 'Job Type is required!';
        error.push({ key: 'POType', value: this.jobTypeValueError });
      }
      if (!this.supplierValue) {
        this.supplierValueError = 'Supplier is required!';
        error.push({ key: 'supplier', value: this.supplierValueError });
      }

      const total = this.doListRaw
        .filter((item) => item.action != 'D')
        .reduce((acc, item) => acc + parseFloat(item.Qty), 0);
      const orderedTotal = parseFloat(this.productQuantityTotal);
      const partialDelivery = total > 0 && total < orderedTotal;

      if (total > orderedTotal) {
        error.push({ key: 'qty', value: 'DO Quantity cannot exceed Product Quantity!' });
      }

      if (error.length > 0 && action != 'D') {
        this.popupEntryRequiredError = error;
        return;
      }

      if (action != 'D' && partialDelivery) {
        const partialDeliveryItems = this.getPartialDeliveryItems();
        const proceed = await this.confirmPartialDelivery(partialDeliveryItems);
        if (!proceed) return;
      }

      const { 'PO #': PONo } = this.purchaseOrderTableSelectedRows[0] || {};

      const requestBody = {
        action: action,
        pono: PONo ?? '',
        date: new Date(this.poDateValue).formatString('yyyy-MM-ddTHH:mm'),
        jobtype: this.jobTypeValue.JobTypeID,
        refno: this.refNoValue,
        supplier: this.supplierValue['Supplier Code'],
        remarks: this.remarksValue,
        amount: this.totalAmountValue,
        products: this.productList
          .filter((item) => item.action)
          .map((item) => {
            const {
              PONo,
              SeqNo,
              Quantity,
              UnitPrice,
              Discount,
              ItemTypeIndicator,
              ProdCode,
              Grade,
              GST2TaxCode,
              GST2TaxRate,
              Taxable_AmountGST2,
              Remark,
              ItemTxnID,
              HeaderTxnID,
              action,
            } = item;

            return {
              pono: PONo ?? '',
              seqno: SeqNo,
              itemtxnid: ItemTxnID ?? null,
              prodcode: ProdCode ?? '',
              grade: Grade ?? '',
              quantityorder: Quantity ?? '0',
              sellingprice: UnitPrice ?? null,
              discount: Discount ?? '0',
              itemtypeindicator: ItemTypeIndicator ?? '0',
              remark: Remark ?? '',
              action: action,
              gst2taxcode: GST2TaxCode ?? '',
              gst2taxrate: GST2TaxRate ?? '0',
              taxableamountgst2: Taxable_AmountGST2 ?? '0',
              headertxnid: HeaderTxnID || null,
            };
          }),
        do: this.doListRaw
          .filter((item) => item.action)
          .map((item) => {
            const {
              PONo,
              SeqNo,
              CompanyName,
              Contact,
              Addr,
              PhoneNo1,
              PhoneNo2,
              VehicleNo,
              DriverName,
              DriverIC,
              ReqDate,
              ItemTxnID,
              ProdCode,
              Grade,
              Qty,
              ItemTypeIndicator,
              action,
            } = item;

            return {
              pono: PONo ?? '',
              companyname: CompanyName ?? '',
              contact: Contact ?? '',
              addr: Addr ?? '',
              phoneno1: PhoneNo1 ?? '',
              phoneno2: PhoneNo2 ?? '',
              vehicleno: VehicleNo ?? '',
              drivername: DriverName ?? '',
              driveric: DriverIC ?? '',
              reqdate: ReqDate ? new Date(ReqDate).formatString('yyyy-MM-dd') : null,
              action: action,
              itemtxnid: ItemTxnID ?? null,
              prodcode: ProdCode ?? '',
              grade: Grade ?? '',
              qty: Qty ?? '0',
              seqno: SeqNo,
              itemtypeindicator: ItemTypeIndicator ?? '0',
            };
          }),

        strdept: this.jobTypeValue.JobHeader,
      };

      this.$fullscreenLoading('Saving Purchase Order...');
      const result = await this.$apiCallV2('/PurchaseOrder/SavePO', requestBody);

      const { Results, ErrMsg, pono } = result?.[0] || {};

      if (Results == '1') {
        this.popupEntryErrorMessages.push(ErrMsg);
        this.$fullscreenLoading();
        return;
      }
      this.populatePopupFields(pono);
      this.openPopup();
      this.menuMode = 'View';
      this.viewMode = true;
      this.popupEntrySuccessMessages.push(ErrMsg);
      this.populatePopupFields(pono);
      this.getPurchaseOrderList();
      this.$fullscreenLoading();
    },

    //#region helpers

    getPartialDeliveryItems() {
      const items = this.productList
        .filter((item) => item.action != 'D')
        .reduce((result, item) => {
          const key = item.ItemTxnID ?? item.SeqNo;
          const existing = result.find((product) => product.key == key);

          if (existing) {
            existing.ordered += parseFloat(item.Quantity || 0);
            return result;
          }

          result.push({
            key,
            name: [item.ProdCode, item.Name].filter((value) => value).join(' - ') || 'Product',
            delivered: 0,
            ordered: parseFloat(item.Quantity || 0),
          });
          return result;
        }, []);

      this.doListRaw
        .filter((item) => item.action != 'D')
        .forEach((item) => {
          const key = item.ItemTxnID ?? item.SeqNo;
          const product = items.find((item) => item.key == key);
          if (product) product.delivered += parseFloat(item.Qty || 0);
        });

      return items.filter((item) => item.delivered < item.ordered);
    },

    async confirmPartialDelivery(partialDeliveryItems) {
      const escapeHtml = (value) => String(value).replace(/[&<>"']/g, (character) => {
        const entities = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
        return entities[character];
      });
      const productLines = partialDeliveryItems
        .map((item) => `${item.name}: ${Number(item.delivered)} / ${Number(item.ordered)}`)
        .join('\n');
      const deliverySummary = ['Delivery progress:', '', productLines].join('\n');
      const remarks = String(this.remarksValue || '').trim();
      if (!remarks) {
        this.remarksValueError = 'Remarks are required for partial delivery';
        this.$nextTick(() => {
          if (this.$refs.remarks) this.$refs.remarks.setFocus();
        });
        return false;
      }

      const content = `<div style="text-align: left; padding: 0 24px; white-space: pre-wrap; word-break: break-word;">${escapeHtml(
        `${deliverySummary}\n\nPartial delivery remarks: ${remarks}`,
      )}</div>`;

      const proceed = await this.$showAlert(
        'warning',
        'Partial Delivery',
        content,
        [
          { text: 'Save Partial Delivery', color: 'Red', textColor: 'white' },
          { text: 'Cancel' },
        ],
      );

      return proceed == 'Save Partial Delivery';
    },

    async warnUnsavedChanges() {
      if (this.viewMode) return true;
      const discard = await this.$showAlert(
        'warning',
        'Discard Changes',
        'You have unsaved changes.<br>Do you wish to discard the changes?',
        [{ text: 'Discard', color: 'Red', textColor: 'white' }, { text: 'Cancel' }],
      );

      if (discard == 'Cancel') return false;
      return true;
    },

    filterDOProductList(items) {
      return items.filter((item) => item.action != 'D');
    },

    convertToArray(item) {
      if (!item) return [];
      return Array.isArray(item) ? item : [item];
    },

    discountValidator() {
      const value = this.discountValue;
      if (value === null || value === undefined) return '';

      let str = String(value).trim();

      // Remove all invalid characters except digits and dots
      str = str.replace(/[^0-9.]/g, '');

      // If the string starts with a dot, prefix with 0 (e.g. ".12" -> "0.12")
      if (str.startsWith('.')) {
        str = '0' + str;
      }

      // If ends with a dot (e.g. "12.") -> remove trailing dot
      if (str.endsWith('.')) {
        str = str.slice(0, -1);
      }

      // Handle multiple dots -> keep only the first one
      const parts = str.split('.');
      if (parts.length > 2) {
        str = parts[0] + '.' + parts.slice(1).join('').replace(/\./g, '');
      }

      let num = parseFloat(str);

      // If still not a number, reset to 0
      if (isNaN(num)) {
        this.discountValue = '0';
        return;
      }

      // Clamp between 0 and 100
      if (num < 0) num = 0;
      if (num > 100) num = 100;

      // Limit to 2 decimal places
      num = Math.floor(num * 100) / 100;

      // Return as string (no trailing zeros)
      this.discountValue = num.toString();
    },

    isFullDelivery(items) {
      const total = items
        .filter((item) => item.action != 'D')
        .reduce((acc, item) => acc + parseFloat(item.Quantity), 0);
      return total == parseFloat(this.productQuantityTotal);
    },

    //#endregion

    async deletePopupButtonClicked(buttonText) {
      if (buttonText == 'Cancel') {
        this.showDeletePopup = false;
        return;
      }
      if (!this.deleteRemarks) {
        this.deleteRemarksError = 'Reason is required!';
        return;
      }

      this.$fullscreenLoading('Deleting Purchase Order');

      const { 'PO #': pono } = this.purchaseOrderTableSelectedRows[0];
      const result = (
        await this.$apiCallV2('/PurchaseOrder/SavePO', {
          pono: pono,
          action: 'D',
          reason: this.deleteRemarks,
          date: null,
          supplier: '',
        })
      )[0];
      const { Results, ErrMsg } = result || {};

      if (Results == '0') {
        this.$showAlert('success', 'Purchase Order Deleted', ErrMsg, [{ text: 'OK' }]);
      } else {
        this.$showAlert('error', 'Purchase Order Could Not Be Deleted', ErrMsg, [{ text: 'OK' }]);
      }
      this.showDeletePopup = false;
      await this.getPurchaseOrderList();
      this.$fullscreenLoading();
    },

    async getSAS() {
      this.SASControl = await this.$apiCallV2(`/SASControl/GetSASControlAccess?system=FINPAYMENT&page=Purchase Order`);
    },
  },
  async mounted() {
    this.$fullscreenLoading('Getting Purchase Order...');
    const promises = [
      this.getSAS(),
      this.getJobTypeList(),
      this.getSupplierList(),
      this.getWhProduct(),
      this.getLocationList(),
      this.getVehicleList(),
      this.getEmployeeList(),
      this.getPurchaseOrderList(),
      this.getPurchaseOrderProductColumnOrders(),
    ];

    await Promise.all(promises);

    this.$fullscreenLoading();
  },
  watch: {
    doProductTotalQuantity() {
      this.doProductQuantityChange();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';
.main {
  padding: 10px 45px;
  display: flex;
  flex-direction: row;
  font-size: 12px;

  > .left-menu-container {
    height: 100%;
    width: fit-content;
  }

  > .right-tablebox-container {
    height: 100%;
    width: 100%;
    margin-left: 5px;
    padding: 5px;
    border-radius: 5px;
    overflow: hidden;
    background-color: white;
  }
}

.top-info-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  margin: 0 5px;
  gap: 10px;
}

.tab-component-container {
  margin-top: 10px;
  height: 100%;
  .tab-main {
    height: 100%;
  }
  > .tab-component {
    height: 100%;

    .tab-container {
      height: 100%;
    }
  }
}

.table-tab-container {
  display: grid;
  grid-template-columns: 4fr 3fr;
  gap: 10px;
  > *:only-child {
    grid-column: 1/-1;
  }

  .cta-group {
    display: flex;
    gap: 5px;
    justify-content: end;
    margin-bottom: 10px;
    align-items: center;
  }

  .table-container {
    display: flex;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
  }
}

.flex-container {
  display: flex;
  gap: 8px;

  &.flex-column {
    flex-direction: column;
  }
}

.purchase-order-summary {
  border: 1px solid gray;
  border-radius: 5px;
  padding: 5px 10px;
  margin-top: -4px;
  margin-bottom: 4px;
}

.w-100 {
  width: 100%;
}

.total-qty-row {
  display: flex;
  gap: 8px;

  text-align: center;
  align-items: last baseline;

  > div {
    display: flex;
    flex-direction: column;
  }
}

.delivery-info-wrapper {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-bottom: 8px;

  .do-empty-text {
    font-size: 1rem;
    text-align: center;
    position: relative;
    height: 50dvh;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.total-price-wrapper {
  display: flex;
  flex-direction: column;
  gap: 4px;
  .row-total {
    margin-left: auto;
    min-width: 20%;
    padding-left: 4px;
    text-align: end;
  }
  .total-row {
    margin-top: 4px;
    border-top: 1px solid black;
    padding-top: 4px;
  }
}

.address-text {
  padding: 4px 8px;
  resize: none;
  width: 100%;
  &:focus {
    outline: none;
  }
}

.delivery-info-table-div {
  border-bottom: 1px solid lightgray;
  .info-row {
    display: grid;
    grid-template-columns: 64px auto 160px 140px 220px 100px;
    background-color: lightgray;
    gap: 1px;
    > * {
      padding: 4px 8px;
      padding-left: 16px;
      align-items: center;
      display: flex;
      background-color: white;
      > .dropdown-main,
      > .input-main {
        margin-left: -8px;
      }
    }
    button {
      width: 100%;
      margin-left: -4px;
    }
    &.header {
      color: white;
      font-size: 0.8rem;
      > * {
        padding: 8px 16px;
        background-color: #535cda;
      }
    }
    &:nth-child(2n) {
      > * {
        background-color: #e2e4ff;
      }
    }
  }

  &.hide-action {
    .info-row {
      grid-template-columns: 64px auto 160px 140px 220px;
    }
  }
}

.delivery-info-popup {
  position: fixed;
  z-index: 9999;
  background: rgba(0, 0, 0, 50%);
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;

  .delivery-info-popup-inner {
    background: white;
    height: fit-content;
    overflow: auto;
    display: flex;
    flex-direction: column;
    gap: 5px;
    position: relative;
    top: 20px;
    padding: 8px;
    border-radius: 5px;
    width: 100%;
    max-width: 1000px;
  }

  .doProductListContainer {
    min-height: 350px;
  }
}

.flex-1 {
  flex: 1;
}

.error-message {
  color: red;
}
</style>
